import { getCurrentInstance, ref, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { LocationQuery, RouteLocationNormalizedLoaded, Router } from 'vue-router'
import http from '@/js/utils/http'
import API from '@/js/const/api'
import APP from '@/js/const/app'
import { formatQueryToArray } from '@/js/utils/filters'

type CameraListRoute = Pick<RouteLocationNormalizedLoaded, 'params' | 'query'>

type CameraListQueryParams = {
  filter: string[]
  tags: string[]
  func: string[]
  watch_status?: string | LocationQuery[string]
}

export type UseCameraListReturn = {
  isLoaded: Ref<boolean>
  currentPage: Ref<number>
  totalCameras: Ref<number | null>
  cameraStatus: Ref<Record<string, unknown>>
  pageSize: Ref<number>
  cameraList: Ref<unknown[] | null>
  checkedCameras: Ref<unknown[]>
  toggleTagSettingDialogFlag: Ref<boolean>
  getCameras: (targetRoute?: CameraListRoute | null) => Promise<void>
  changePage: (page: number) => void
  setTag: (cameraData: unknown) => void
}

function toQueryArray(value: unknown): string[] {
  if (value === undefined || value === null) {
    return []
  }
  return formatQueryToArray(value as string | string[]) || []
}

/**
 * カメラ一覧取得・ページング・タグ設定対象の共通処理
 * （旧 CameraListMixin の置き換え）
 */
export function useCameraList(): UseCameraListReturn {
  const instance = getCurrentInstance()
  const injectedRoute = useRoute()
  const injectedRouter = useRouter()

  // ユニットテストで $route/$router をモックする場合に備え、proxy を優先する
  const getRoute = (): CameraListRoute => {
    const proxy = instance?.proxy as { $route?: CameraListRoute } | undefined
    return proxy?.$route ?? injectedRoute
  }

  const getRouter = (): Router => {
    const proxy = instance?.proxy as { $router?: Router } | undefined
    return proxy?.$router ?? injectedRouter
  }

  const isLoaded = ref(false)
  // 表示中のページ
  const currentPage = ref(1)
  // 全カメラ件数
  const totalCameras = ref<number | null>(null)
  // カメラの状態
  const cameraStatus = ref<Record<string, unknown>>({})
  // 1ページに表示するカメラ数
  const pageSize = ref(APP.PAGE_LIMIT)
  // カメラデータ
  const cameraList = ref<unknown[] | null>(null)
  // 選択したカメラ
  const checkedCameras = ref<unknown[]>([])
  // タグ設定用ダイアログ表示用フラグ
  const toggleTagSettingDialogFlag = ref(false)

  /**
   * カメラ一覧を取得
   * @param targetRoute urlパラメータのデータ
   */
  const getCameras = (targetRoute?: CameraListRoute | null) => {
    isLoaded.value = false
    // 表示中のページを設定。URLパラメータにページ番号が連携されていない場合は1を設定
    currentPage.value = targetRoute ? Number(targetRoute.params.page) : currentPage.value

    const extraParams: CameraListQueryParams = {
      filter: [],
      tags: [],
      func: [],
    }
    // パラメータがない場合URLのqueryを使う
    const query = targetRoute ? targetRoute.query : getRoute().query
    if (query.filter || query.tags || query.func || query.watch_status) {
      // queryが一つの場合stringなのでarrayに変換
      extraParams.filter = toQueryArray(query.filter)
      extraParams.tags = toQueryArray(query.tags)
      extraParams.func = toQueryArray(query.func)
      if (query.watch_status) {
        extraParams.watch_status = query.watch_status
      }
    }

    const params = Object.assign(
      {
        page: currentPage.value,
        count: pageSize.value,
      },
      extraParams
    )

    // 処理待ちのリクエストをキャンセルする(連続実行された場合に最後のリクエストのみ実行する)
    http.cancel(`${API.CAMERAS}_get`)

    return http.get(API.CAMERAS, params).then((res) => {
      cameraList.value = res.data.data.cameras
      totalCameras.value = res.data.data.total
      cameraStatus.value = res.data.data.watch
      isLoaded.value = true
    })
  }

  /**
   * ページの変更
   * @param page 表示するページ番号
   */
  const changePage = (page: number) => {
    getRouter().push({
      name: 'list',
      params: { page: page },
      query: getRoute().query,
    })
  }

  /**
   * カメラ個別のタグ設定用
   * @param cameraData カメラデータ
   */
  const setTag = (cameraData: unknown) => {
    checkedCameras.value = [cameraData]
    toggleTagSettingDialogFlag.value = true
  }

  return {
    isLoaded,
    currentPage,
    totalCameras,
    cameraStatus,
    pageSize,
    cameraList,
    checkedCameras,
    toggleTagSettingDialogFlag,
    getCameras,
    changePage,
    setTag,
  }
}
